// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useRouter } from 'vue-router';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import {
  isStepTwoActivated,
  isStepThreeActivated,
  isAtcivatedWatch,
} from '@/refs/commerce.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { CommerceService } from '@/services/commerce.services';
import { useDialogStore } from '@/store/dialog.store';
import { useManageOrganisationStore } from '@/store/manageOrganisation.store';
import {
  SingleOrganisation,
  organizationIdCommerce,
} from '@/refs/commerce.refs';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  IAddStoreContactPayload,
  IApiResponseCommerceSlugs,
  ICommerceBody,
  ICommerceBodySlug,
  ICommerceBodyUpdate,
  ICommerceLogo,
  IContact,
} from '@/types/commerce.types';

/**
 * Hook pour gérer les mutations liées au commerce.
 * Fournit des fonctions pour créer un commerce, ajouter contacts, logos, et supprimer un commerce.
 * Gère également les étapes du formulaire et les erreurs globales.
 */
export const CommerceMutation = () => {
  const router = useRouter();
  const commerceStore = useCommerceStore();
  const dialogStore = useDialogStore();
  const organisationStore = useManageOrganisationStore();
  // -------------------------------
  // 🔹 Gestion des erreurs globale
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0];

    isGlobalError.value = true;
    isGlobalError.value = true;
    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    isAtcivatedWatch.value = !isAtcivatedWatch.value;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un commerce
  // -------------------------------
  const createCommerce = useMutation<any, Error, ICommerceBody>({
    mutationFn: (payload: ICommerceBody) =>
      commerceStore.createCommerce(payload),
    onSuccess: async () => {
      // Mettre à jour le storeCount dans SingleOrganisation (badgeProfileOrg)
      const orgId = organizationIdCommerce.value;
      if (orgId && SingleOrganisation.value[0]) {
        try {
          const updated = await organisationStore.getOrganisationById(orgId);
          (SingleOrganisation.value[0] as any).storeCount =
            updated.storeCount ?? 0;
        } catch {
          // fallback : incrémenter localement
          (SingleOrganisation.value[0] as any).storeCount =
            ((SingleOrganisation.value[0] as any).storeCount ?? 0) + 1;
        }
      }
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Création d'un contact pour le commerce
  // -------------------------------
  const createCommerceContact = useMutation<any, Error, IContact>({
    mutationFn: (payload: IContact) =>
      commerceStore.createCommerceContact(payload),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Ajout d'un logo pour le commerce
  // -------------------------------
  const createCommerceLogo = useMutation<any, Error, ICommerceLogo>({
    mutationFn: (payload: ICommerceLogo) =>
      commerceStore.createCommerceLogo(payload),
    onSuccess: () => {
      // Termine le processus d'ajout
      isStepTwoActivated.value = false;
      isStepThreeActivated.value = false;
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour d'un commerce par ID
  // -------------------------------
  const updateCommerce = useMutation<any, Error, ICommerceBodyUpdate>({
    mutationFn: (payload: ICommerceBodyUpdate) => {
      return commerceStore.UpdateCommerce(payload);
    },
    onSuccess: async (data) => {
      // Mettre à jour le store ou notifier l'utilisateur
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour d'un commerceConatct par ID
  // -------------------------------
  const updateCommerceContact = useMutation<any, Error, any>({
    mutationFn: (payload: any) => {
      return commerceStore.UpdateCommerceContact(payload);
    },
    onSuccess: async (data) => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Suppression d'un commerce
  // -------------------------------
  const DeleteCommerce = useMutation<any, Error, string>({
    mutationFn: (payload: string) => commerceStore.DeleteCommerce(payload),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });
  // -------------------------------
  // 🔹 Suppression d'un commerce conatct
  // -------------------------------
  const DeleteCommerceContact = useMutation<any, Error>({
    mutationFn: () => commerceStore.DeleteCommerceContact(),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Cette fonction appelle le store de création de slug
  // -------------------------------
  const createCommerceSlug = useMutation<any, Error, ICommerceBodySlug>({
    mutationFn: (payload: ICommerceBodySlug) =>
      commerceStore.createCommerceSlug(payload),
    onSuccess: () => {
      // Passe à l'étape 2 du formulaire
      // isStepTwoActivated.value = true;
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Cette fonction appelle le store de suppression de slug
  // -------------------------------
  const updateCommerceSlug = useMutation<any, Error, any>({
    mutationFn: (payload: any) => commerceStore.updateCommerceSlug(payload),
    onSuccess: () => {
      // Passe à l'étape 2 du formulaire
      // isStepTwoActivated.value = true;
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Ajout d'un contact à un restaurant
  // -------------------------------
  const addStoreContact = useMutation<any, Error, IAddStoreContactPayload>({
    mutationFn: (payload: IAddStoreContactPayload) =>
      commerceStore.addStoreContact(payload),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  const deleteCommerceSlug = useMutation<any, Error>({
    mutationFn: () => commerceStore.deleteCommerceSlug(),
    onSuccess: () => {
      // Passe à l'étape 2 du formulaire
      // isStepTwoActivated.value = true;
    },
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  const handleCreateCommerce = (item: ICommerceBody) => {
    return createCommerce.mutateAsync(item);
  };
  const handleCreateCommerceSlug = (item: ICommerceBodySlug) => {
    return createCommerceSlug.mutateAsync(item);
  };
  const handleCreateCommerceContact = (item: IContact) =>
    createCommerceContact.mutateAsync(item);
  const handleCreateCommerceLogo = (item: ICommerceLogo) =>
    createCommerceLogo.mutate(item);

  const handleUpdateCommerce = (item: ICommerceBodyUpdate) => {
    return updateCommerce.mutateAsync(item);
  };
  const handleUpdateCommerceContact = (item: any) => {
    return updateCommerceContact.mutateAsync(item);
  };
  const handleDeleteCommerce = (item: string) =>
    DeleteCommerce.mutateAsync(item);
  const handleDeleteCommerceContact = () => DeleteCommerceContact.mutateAsync();
  const handleUpdateCommerceSlug = (data: any) => {
    return updateCommerceSlug.mutateAsync(data);
  };

  const handleDeleteCommerceSlug = () => {
    return deleteCommerceSlug.mutateAsync();
  };

  const handleAddStoreContact = (item: IAddStoreContactPayload) =>
    addStoreContact.mutateAsync(item);

  const handleAddStoreContactDirect = (
    restaurantId: string,
    data: IAddStoreContactPayload,
  ) => CommerceService.addStoreContactDirect(restaurantId, data);

  const handleUpdateStoreContact = (
    restaurantId: string,
    contactId: string,
    data: IAddStoreContactPayload,
  ) => CommerceService.updateStoreContact(restaurantId, contactId, data);

  return {
    handleCreateCommerce,
    handleCreateCommerceSlug,
    handleCreateCommerceContact,
    handleCreateCommerceLogo,
    handleUpdateCommerce,
    handleDeleteCommerce,
    handleUpdateCommerceContact,
    handleDeleteCommerceContact,
    handleDeleteCommerceSlug,
    handleUpdateCommerceSlug,
    handleAddStoreContact,
    handleAddStoreContactDirect,
    handleUpdateStoreContact,
  };
};
